// Centralized API service for all API calls

const GEO_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API_URL = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise} Geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEO_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation API error:', error);
    throw error;
  }
};

/**
 * Get country dial code from country code
 * @param {string} countryCode - Two-letter country code
 * @returns {number} Dial code
 */
const getDialCode = (countryCode) => {
  const dialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'UK': 44, 'IN': 91, 'AU': 61, 'JP': 81,
    'DE': 49, 'FR': 33, 'IT': 39, 'ES': 34, 'NL': 31, 'PK': 92,
    'CN': 86, 'BR': 55, 'MX': 52, 'RU': 7, 'KR': 82, 'SA': 966,
    'AE': 971, 'SG': 65, 'MY': 60, 'TH': 66, 'PH': 63, 'ID': 62
  };
  return dialCodes[countryCode] || 1;
};

/**
 * Submit contact form to Lead API
 * @param {Object} formData - Form data from contact form
 * @param {Object} geoData - Geolocation data (optional)
 * @returns {Promise} API response
 */
export const submitContactForm = async (formData, geoData = null) => {
  try {
    // Get current page URL
    const pageUrl = window.location.href;

    // Extract only numeric phone number (remove any formatting)
    const phoneNumber = formData.phone.replace(/\D/g, '');

    // Extract dial code number from country code (remove + sign)
    const countryDialCode = parseInt(formData.countryCode.replace('+', ''));

    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: geoData?.data?.countryCode || formData.country || 'US',
      phoneNumber: phoneNumber,
      ipAddress: geoData?.data?.ipAddress || '',
      description: formData.message || '',
      pageUrl: pageUrl,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: '',
      packageAmount: 0
    };

    console.log('Submitting form with payload:', payload);

    const response = await fetch(LEAD_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    // Handle different status codes
    if (response.status === 200 && result.isSuccess) {
      // Success - redirect to application page
      const customerId = result.data?.customerId;
      if (customerId) {
        window.location.href = `/application/main?customerid=${customerId}`;
      }
      return result;
    } else if (response.status === 401) {
      // Unauthorized error
      throw new Error(result.message || 'Unauthorized access');
    } else if (response.status === 400) {
      // Validation error
      throw new Error(result.errorMessage || 'Invalid form data');
    } else {
      // Other errors
      throw new Error(result.errorMessage || result.message || 'Failed to submit form');
    }
  } catch (error) {
    console.error('Form submission error:', error);
    throw error;
  }
};

/**
 * Initialize form with geolocation data
 * @returns {Promise} Geolocation data
 */
export const initializeFormWithGeo = async () => {
  try {
    const geoData = await fetchGeolocation();
    return geoData;
  } catch (error) {
    console.error('Failed to initialize geolocation:', error);
    return null;
  }
};
